import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Small wrapper around shadcn's AlertDialog that gives us a controlled,
 * imperative-feeling confirm flow:
 *
 *   <ConfirmDialog
 *     open={openState}
 *     onOpenChange={setOpenState}
 *     title="Remove this service?"
 *     description="This cannot be undone."
 *     confirmText="Remove"
 *     destructive
 *     onConfirm={async () => { ... }}
 *   />
 *
 * Always renders a Cancel button. Confirm button is destructive-styled when
 * `destructive` is true.
 */
const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = null,
  confirmText = "Confirm",
  cancelText = "Cancel",
  destructive = false,
  onConfirm,
  testId = "confirm-dialog",
}) => {
  const handleConfirm = async (e) => {
    e.preventDefault();
    if (onConfirm) await onConfirm();
    onOpenChange && onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="bg-[#161620] border border-purple-500/40 text-white"
        data-testid={testId}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription className="text-gray-400">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="bg-transparent border-gray-700 text-white hover:bg-gray-800"
            data-testid={`${testId}-cancel`}
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={
              destructive
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-purple-600 hover:bg-purple-500 text-white"
            }
            data-testid={`${testId}-confirm`}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
